// The warehouse form, shared by the creation modal (WarehousesInstructions.js)
// and the edit card of the detail page (WarehouseDetail.js): name, contact
// addresses, the AI's instructions, the associated B2B clients.
import React, { useState } from 'react';
import { Check, Plus, Search, X } from 'lucide-react';
import { Field } from '../design/ui';
import './WarehouseForm.css';

// Multi-address editor: always at least one (empty) row.
export const EmailListEditor = ({ emails, onChange, t, idPrefix = 'wh' }) => {
  const list = emails.length > 0 ? emails : [''];
  const setAt = (index, value) => onChange(list.map((e, i) => (i === index ? value : e)));
  const removeAt = (index) => {
    const next = list.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : ['']);
  };
  return (
    <div className="wh-form__emails">
      {list.map((email, index) => (
        <div key={index} className="wh-form__email-row">
          <input
            id={index === 0 ? `${idPrefix}-email` : undefined}
            type="email"
            className="u-input"
            value={email}
            onChange={(e) => setAt(index, e.target.value)}
            placeholder="contact@entrepot.fr"
            aria-label={t('aiAutomationUi.warehouses.form.emailN', 'Adresse {{n}}', { n: index + 1 })}
          />
          <button
            type="button"
            className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
            onClick={() => removeAt(index)}
            disabled={list.length === 1 && !list[0]}
            aria-label={t('warehousesInstructions.removeEmail', 'Retirer cet email')}
            title={t('warehousesInstructions.removeEmail', 'Retirer cet email')}
          >
            <X aria-hidden="true" />
          </button>
        </div>
      ))}
      <button type="button" className="u-btn u-btn--ghost u-btn--sm wh-form__add" onClick={() => onChange([...list, ''])}>
        <Plus aria-hidden="true" />
        {t('aiAutomationUi.warehouses.form.addEmail', 'Ajouter une adresse')}
      </button>
    </div>
  );
};

// B2B client picker: search + toggles, the count of the selection.
export const ClientPicker = ({ clients, selectedIds, onToggle, t }) => {
  const [query, setQuery] = useState('');
  const q = query.toLowerCase();
  const filtered = clients.filter((client) => (client.name || '').toLowerCase().includes(q)
    || (client.client_id || '').toLowerCase().includes(q));
  return (
    <div className="wh-form__picker">
      <div className="wh-form__picker-head">
        <div className="u-input-group u-input-group--lead">
          <Search className="u-input-group__icon" aria-hidden="true" />
          <input
            type="search"
            className="u-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('warehousesInstructions.searchClients', 'Rechercher un client…')}
            aria-label={t('warehousesInstructions.searchClients', 'Rechercher un client…')}
          />
        </div>
        <span className="wh-form__picker-count u-num">
          {t('aiAutomationUi.warehouses.form.selected', '{{count}} sélectionnés', { count: selectedIds.length })}
        </span>
      </div>
      <div className="wh-form__picker-list">
        {filtered.length === 0 ? (
          <div className="wh-form__picker-empty">
            {clients.length === 0
              ? t('aiAutomationUi.warehouses.form.noClients', 'Aucun client B2B : ajoutez-les depuis la page Clients B2B.')
              : t('warehousesInstructions.noClientsFound', 'Aucun client trouvé')}
          </div>
        ) : filtered.map((client) => {
          const isOn = selectedIds.includes(client.id);
          return (
            <button
              key={client.id}
              type="button"
              className={`wh-form__option${isOn ? ' is-on' : ''}`}
              aria-pressed={isOn}
              onClick={() => onToggle(client.id)}
            >
              <span className="wh-form__check" aria-hidden="true">{isOn && <Check />}</span>
              <span className="wh-form__option-name">{client.name}</span>
              {client.client_id && <span className="u-code">{client.client_id}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// The whole form. `setForm(updater)` receives a function of the previous form.
const WarehouseForm = ({ form, setForm, clients, t, idPrefix = 'wh' }) => {
  const toggleClient = (clientId) => setForm((prev) => ({
    ...prev,
    clientIds: prev.clientIds.includes(clientId)
      ? prev.clientIds.filter((id) => id !== clientId)
      : [...prev.clientIds, clientId],
  }));
  return (
    <div className="u-form">
      <Field label={t('aiAutomationUi.warehouses.form.name', 'Nom')} htmlFor={`${idPrefix}-name`}>
        <input
          id={`${idPrefix}-name`}
          type="text"
          className="u-input"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder={t('warehousesInstructions.namePlaceholder', 'Ex. : Entrepôt Lyon Sud')}
        />
      </Field>
      <Field
        label={t('aiAutomationUi.warehouses.form.contact', 'Adresses de contact')}
        htmlFor={`${idPrefix}-email`}
        help={t('warehousesInstructions.sectionContactHint', 'Adresses utilisées par l’IA pour contacter l’entrepôt.')}
      >
        <EmailListEditor emails={form.emails} onChange={(emails) => setForm((f) => ({ ...f, emails }))} t={t} idPrefix={idPrefix} />
      </Field>
      <Field
        label={t('warehousesInstructions.sectionInstructions', 'Instructions IA')}
        htmlFor={`${idPrefix}-instructions`}
        help={t('warehousesInstructions.sectionInstructionsHint', 'Consignes suivies par l’IA lors des échanges avec cet entrepôt.')}
      >
        <textarea
          id={`${idPrefix}-instructions`}
          className="u-textarea"
          rows={8}
          value={form.instructions}
          onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))}
          placeholder={t('warehousesInstructions.instructionsPlaceholder', 'Ex. : Toujours fournir la référence de commande…')}
        />
      </Field>
      <div className="u-field">
        <span className="u-label">{t('warehousesInstructions.sectionClients', 'Clients associés')}</span>
        <ClientPicker clients={clients} selectedIds={form.clientIds} onToggle={toggleClient} t={t} />
        <p className="u-help">{t('aiAutomationUi.warehouses.form.clientsHelp', 'Les clients B2B dont les colis partent de cet entrepôt.')}</p>
      </div>
    </div>
  );
};

export default WarehouseForm;
